/**
 * Generates the business header HTML for print pages.
 * Standard Israeli business document header with logo, name, ID, address, phone, fax, email.
 * Returns empty string if no business profile is set.
 */
export const generateBusinessHeader = (businessProfile) => {
  if (!businessProfile || !businessProfile.business_name) return '';

  const bp = businessProfile;
  const businessType = bp.business_type || 'ח.פ';

  // Right side: logo + name + type/ID
  const logoImg = bp.logo_url
    ? `<img src="${bp.logo_url}" alt="" style="max-height:60px;max-width:75px;object-fit:contain;display:inline-block;vertical-align:middle;margin-left:14px;" />`
    : '';

  const idText = bp.business_id
    ? `<div style="font-size:14px;color:#333;margin-top:3px;font-weight:600;">${businessType} ${bp.business_id}</div>`
    : '';

  // Left side: contact details — bold values
  const leftLines = [];
  if (bp.address) leftLines.push(`<div style="margin-bottom:3px;font-size:14px;color:#222;font-weight:500;">${bp.address}</div>`);
  if (bp.phone) leftLines.push(`<div style="margin-bottom:3px;font-size:14px;color:#222;">טל: <strong>${bp.phone}</strong></div>`);
  if (bp.fax) leftLines.push(`<div style="margin-bottom:3px;font-size:14px;color:#222;">פקס: <strong>${bp.fax}</strong></div>`);
  if (bp.email) leftLines.push(`<div style="font-size:14px;color:#222;font-weight:500;">${bp.email}</div>`);

  return `
    <table dir="rtl" style="width:100% !important;border:none !important;border-collapse:collapse;margin-bottom:6px;">
      <tr style="border:none !important;">
        <td style="vertical-align:middle;text-align:right;padding:0 0 12px 0;border:none !important;border-bottom:none !important;">
          ${logoImg}<span style="font-size:24px;font-weight:800;color:#111;vertical-align:middle;letter-spacing:-0.3px;">${bp.business_name}</span>
          ${idText}
        </td>
        <td style="vertical-align:middle;text-align:left;padding:0 0 12px 0;border:none !important;border-bottom:none !important;line-height:1.5;">
          ${leftLines.join('')}
        </td>
      </tr>
    </table>
    <hr style="border:none;border-top:4px solid #111;margin:0 0 20px 0;" />
  `;
};

/**
 * CSS that prints "עמוד X מתוך Y" centered in the bottom margin of every page.
 * Uses the CSS Paged Media `@page` margin box with `counter(page)` / `counter(pages)`,
 * supported by Chromium and Firefox. The `margin-bottom` reserves vertical space so
 * the footer never overlaps content, regardless of any `@page { margin }` set by callers.
 */
const PAGINATION_STYLE = `
<style id="bp-print-pagination">
  @media print {
    @page {
      margin-bottom: 18mm;
      @bottom-center {
        content: "עמוד " counter(page) " מתוך " counter(pages);
        font-family: Arial, sans-serif;
        font-size: 11px;
        color: #555;
      }
    }
  }
</style>
`;

/**
 * Injects the pagination stylesheet into the print HTML.
 * Appends to <head> so it overrides any earlier `@page` margin-bottom set by callers.
 * If no <head> exists, wraps the content in a minimal HTML skeleton.
 */
const injectPagination = (htmlContent) => {
  if (htmlContent.includes('id="bp-print-pagination"')) return htmlContent;
  if (/<\/head>/i.test(htmlContent)) {
    return htmlContent.replace(/<\/head>/i, `${PAGINATION_STYLE}</head>`);
  }
  if (/<body[^>]*>/i.test(htmlContent)) {
    return htmlContent.replace(/<body([^>]*)>/i, `<head>${PAGINATION_STYLE}</head><body$1>`);
  }
  return `<!DOCTYPE html><html dir="rtl"><head>${PAGINATION_STYLE}</head><body>${htmlContent}</body></html>`;
};

/**
 * Inline script injected into every print popup. Runs in the popup's own
 * window context, so its lifecycle survives the parent navigating away.
 *
 * Triggers `window.print()` exactly once on load, then closes the popup on
 * the first signal that the print dialog has been dismissed (whether the
 * user clicked Print or Cancel). Three close signals are wired so at least
 * one fires reliably across Chrome, Safari, Firefox, and mobile WebViews:
 *
 *   1. `onafterprint` — most browsers, but unreliable on Safari/iOS.
 *   2. `matchMedia('print').change` — fires when print mode exits. The most
 *      portable signal; covers the cancel path on Safari.
 *   3. Window `focus` after print() — last-resort, in case the two above
 *      don't fire (older WebViews). Wait a tick so we don't close the
 *      window before the dialog actually appears.
 */
const SELF_PRINT_SCRIPT = `
<script>
(function () {
  var printed = false;
  var closed = false;
  function safeClose() {
    if (closed) return;
    closed = true;
    try { window.close(); } catch (e) {}
  }
  function triggerPrint() {
    if (printed) return;
    printed = true;
    try { window.focus(); } catch (e) {}
    try { window.print(); } catch (e) { safeClose(); }
  }
  // Signal #1: afterprint event
  window.addEventListener('afterprint', safeClose);
  // Signal #2: matchMedia('print') change — fires when leaving print mode
  try {
    var mql = window.matchMedia('print');
    var onChange = function (e) { if (!e.matches) safeClose(); };
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
  } catch (e) {}
  // Signal #3: focus after print() — covers WebViews where neither of the
  // above fire. Skip the focus that arrives BEFORE print() actually runs.
  window.addEventListener('focus', function () {
    if (printed) setTimeout(safeClose, 300);
  });
  // Fire the print dialog once content has rendered.
  if (document.readyState === 'complete') {
    setTimeout(triggerPrint, 50);
  } else {
    window.addEventListener('load', function () { setTimeout(triggerPrint, 50); });
  }
})();
</script>
`;

/**
 * Injects the self-print script just before </body>. If the HTML doesn't have
 * a body tag, appends to the end.
 */
const injectSelfPrint = (htmlContent) => {
  if (htmlContent.includes('id="bp-print-self"')) return htmlContent;
  const marker = SELF_PRINT_SCRIPT.replace('<script>', '<script id="bp-print-self">');
  if (/<\/body>/i.test(htmlContent)) {
    return htmlContent.replace(/<\/body>/i, `${marker}</body>`);
  }
  return htmlContent + marker;
};

/**
 * Opens a print window that works on both desktop and mobile.
 * - Desktop: opens a popup; the popup auto-prints AND auto-closes on dismiss
 * - Mobile: opens blob URL in new tab so the OS native share/print sheet runs
 *
 * The print + close lifecycle runs ENTIRELY inside the popup via an injected
 * script (see SELF_PRINT_SCRIPT), so the parent can navigate away immediately
 * without breaking the popup's behaviour.
 */
export const openPrintWindow = (htmlContent) => {
  htmlContent = injectPagination(htmlContent);
  htmlContent = injectSelfPrint(htmlContent);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    // Inject responsive viewport + mobile-friendly styles on top of the
    // self-print script already added above.
    let mobileContent = htmlContent;
    if (!mobileContent.includes('name="viewport"')) {
      mobileContent = mobileContent.replace(
        '<head>',
        `<head><meta name="viewport" content="width=device-width, initial-scale=1"><style>
          body { font-size: 14px; padding: 8px; }
          table { font-size: 13px; word-break: break-word; }
          img { max-width: 100%; height: auto; }
        </style>`
      );
    }
    const blob = new Blob([mobileContent], { type: 'text/html; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } else {
    // Use a named window to avoid opening multiple tabs when print is
    // re-clicked. The popup is self-driving — no parent-side print() calls.
    const printWindow = window.open('', 'PrintWindow', 'width=900,height=700,top=50,left=50,toolbar=no,menubar=no');
    if (!printWindow) {
      alert('חלון ההדפסה נחסם. אנא אפשר חלונות קופצים עבור אתר זה.');
      return;
    }
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};
